import type {
	DashboardSellerForOrder,
	sellersForOrderInput,
} from "@/api/custom_types";
import { useApiError } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import RelationNameLink from "@/pages/relations/RelationNameLink";
import { useSellersForOrder } from "@/services";
import { getTimeSinceText } from "@/utils/timestamp";
import { formatNumber } from "@/utils/tools";
import {
	DataTable,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	useServerTableState,
} from "@st-marketplace/ui";
import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo } from "react";

interface AddOrderDataTableProps {
	amount: number;
	pending: boolean;
	selectedRelationId?: string;
	onSelect: (seller: DashboardSellerForOrder) => void;
}

const loadingRows = [
	"first",
	"second",
	"third",
	"fourth",
	"fifth",
	"sixth",
	"seventh",
	"eighth",
	"ninth",
	"tenth",
] as const;
const loadingCells = ["seller", "price", "stock", "lastOrder"] as const;

const AddOrderDataTable: React.FC<AddOrderDataTableProps> = ({
	amount,
	pending,
	selectedRelationId,
	onSelect,
}) => {
	const { t, lang } = useTranslation();
	const { getErrorInterface } = useApiError();

	const server = useServerTableState();
	useEffect(() => server.onPageChange(0), [server.onPageChange]);

	const {
		data: sellers,
		isLoading,
		isFetching,
		error,
		refetch,
	} = useSellersForOrder(
		{
			query: {
				amount: amount * 1000,
				page: server.pageIndex + 1,
				page_size: server.pageSize,
				sort_mode: server.sorting[0]?.desc
					? "desc"
					: server.sorting.length
						? "asc"
						: undefined,
				sort_by:
					(server.sorting[0]?.id as NonNullable<
						sellersForOrderInput["query"]
					>["sort_by"]) || undefined,
			},
		},
		{ enabled: !pending },
	);

	const columns = useMemo<ColumnDef<DashboardSellerForOrder>[]>(
		() => [
			{
				id: "select",
				header: "",
				enableSorting: false,
				cell: ({ row }) => (
					<input
						type="radio"
						name="add-order-seller"
						value={row.original.relation_id}
						checked={selectedRelationId === row.original.relation_id}
						onChange={() => onSelect(row.original)}
						aria-label={t("addOrder.selectSeller", {
							name: row.original.seller_name,
						})}
						className="size-4 accent-primary-100"
					/>
				),
			},
			{
				id: "seller_name",
				header: t("addOrder.tableSellerName"),
				enableSorting: false,
				cell: ({ row }) => (
					<RelationNameLink
						name={row.original.seller_name}
						relationId={row.original.relation_id}
						party="seller"
					/>
				),
			},
			{
				id: "unit_price",
				header: t("addOrder.tableUnitPrice"),
				cell: ({ row }) => `${formatNumber(row.original.unit_price, "price")}`,
			},
			{
				id: "stock",
				header: t("addOrder.tableStock"),
				cell: ({ row }) => formatNumber(row.original.stock.value, "count"),
			},
			{
				id: "last_order_at",
				header: t("addOrder.tableLastOrderAt"),
				cell: ({ row }) =>
					(row.original.last_order_at &&
						getTimeSinceText(row.original.last_order_at, t, lang)) ||
					"-",
			},
		],
		[t, lang, selectedRelationId, onSelect],
	);

	const rows = sellers?.results ?? [];
	useEffect(() => {
		if (
			!pending &&
			!isFetching &&
			!error &&
			sellers?.count === 1 &&
			sellers.results.length === 1
		) {
			const seller = sellers.results[0];
			if (selectedRelationId !== seller.relation_id) onSelect(seller);
		}
	}, [sellers, pending, isFetching, error, selectedRelationId, onSelect]);
	const loading = pending || isLoading || (isFetching && rows.length === 0);

	return (
		<div className="min-w-0 max-w-full">
			{loading ? (
				<div role="status" aria-label={t("common.loading")}>
					<Table aria-hidden="true">
						<TableHeader>
							<TableRow>
								<TableHead className="w-10" />
								<TableHead>{t("addOrder.tableSellerName")}</TableHead>
								<TableHead>{t("addOrder.tableUnitPrice")}</TableHead>
								<TableHead>{t("addOrder.tableStock")}</TableHead>
								<TableHead>{t("addOrder.tableLastOrderAt")}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{loadingRows.map((row) => (
								<TableRow key={row}>
									<TableCell>
										<Skeleton className="size-4 rounded-full" />
									</TableCell>
									{loadingCells.map((column) => (
										<TableCell key={column} variant="skeleton" />
									))}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			) : (
				<DataTable
					className="max-w-full overflow-x-auto"
					columns={columns}
					data={rows}
					emptyStateProps={{
						mode: "data",
						title: t("addOrder.noSellers"),
						description: t("addOrder.noSellersDescription"),
					}}
					totalItems={sellers?.count ?? 0}
					server={server}
					isLoading={isLoading}
					isFetching={isFetching}
					error={error ? getErrorInterface(error) : null}
					onRetry={refetch}
					getRowId={(row) => row.relation_id}
					onRowClick={onSelect}
				/>
			)}
		</div>
	);
};

export default AddOrderDataTable;
